import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import LabelValuePair from "../common/LabelValuePair";
import TablePagination from "../common/Table/TablePagination";
import { AuditLog, AuditLogTypeLabels } from "../../models/AuditLog";
import { AuditLogsRepository } from "../../repositories/AuditLogsRepository";
import { QUERY_KEYS } from "@/constants/QueryKeys";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { dialogWide } from "@/lib/dialogSize";

// Files are deleted from an entity that stays, so the entry can link to it; a deleted entity has no page left.
const PARENT_ROUTES: Record<string, string> = {
  "case file": "case",
  "intake file": "intake",
  "provider file": "provider",
  "template file": "template",
  "firm document": "firm-documents-folder",
};

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

/**
 * A deletion queued from a Manage or Manage files drawer, with the targets it named.
 */
/*
 * The targets come from the entry's own snapshot, one page at a time: they are exactly what the job deletes, so
 * nothing else is left to read their names from afterwards.
 */
export const ViewDeletionRequestedAuditLogDetailsModal: React.FC<{
  auditLog: AuditLog | null;
  open: boolean;
  onClose: () => void;
  organizationId: string;
}> = ({ auditLog, open, onClose, organizationId }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const repository = new AuditLogsRepository();

  let data: { Target?: string; Mode?: string; Count?: number } | null = null;
  try {
    data = auditLog?.additionalData ? JSON.parse(auditLog.additionalData) : null;
  } catch {
    data = null;
  }

  const targetsQuery = useQuery({
    queryKey: [QUERY_KEYS.DELETION_REQUEST_TARGETS, organizationId, auditLog?.id, page, rowsPerPage],
    enabled: open && !!auditLog?.id,
    queryFn: () => repository.getDeletionRequestTargets(auditLog!.id, page + 1, rowsPerPage),
  });

  const parentRoute = data?.Mode === "Files" && data.Target ? PARENT_ROUTES[data.Target] : undefined;
  const isFiles = data?.Mode === "Files";

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setPage(0);
          onClose();
        }
      }}
    >
      <DialogContent className={`${dialogWide} overflow-y-auto`}>
        {auditLog ? (
          <>
            <DialogHeader>
              <DialogTitle>{AuditLogTypeLabels[auditLog.eventType]}</DialogTitle>
            </DialogHeader>

            <Separator />

            <div className="flex min-w-0 flex-col gap-2 py-2">
              {data ? (
                <>
                  <LabelValuePair label="Deleting" value={data.Target ? `${capitalize(data.Target)}s` : "—"} />
                  <LabelValuePair label="Count" value={(data.Count ?? 0).toLocaleString()} />
                  {parentRoute && auditLog.entityId && (
                    <div className="flex flex-row gap-2 text-sm">
                      <span className="font-bold">Of:</span>
                      <a
                        className="text-primary underline-offset-4 hover:underline"
                        href={`/organization/${organizationId}/${parentRoute}/${auditLog.entityId}`}
                      >
                        {auditLog.entityId}
                      </a>
                    </div>
                  )}
                </>
              ) : (
                <span className="text-sm">Failed to parse additionalData</span>
              )}

              <div className="min-w-0 overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{isFiles ? "File path" : "Name"}</TableHead>
                      <TableHead>Id</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {targetsQuery.isLoading && (
                      <TableRow>
                        <TableCell colSpan={2} className="text-sm text-muted-foreground">Loading…</TableCell>
                      </TableRow>
                    )}
                    {targetsQuery.isError && (
                      <TableRow>
                        <TableCell colSpan={2} className="text-sm text-destructive">
                          Could not load the list of deleted targets.
                        </TableCell>
                      </TableRow>
                    )}
                    {targetsQuery.data?.data.map((target) => (
                      <TableRow key={target.id}>
                        <TableCell className="font-mono text-xs break-all">{target.name}</TableCell>
                        <TableCell className="font-mono text-xs">{target.id}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {(targetsQuery.data?.paging?.totalCount ?? 0) > 100 && (
                <TablePagination
                  page={page}
                  rowsPerPage={rowsPerPage}
                  rows={targetsQuery.data?.paging?.totalCount ?? 0}
                  onPageChange={setPage}
                  onRowsPerPageChange={(value) => {
                    setRowsPerPage(value);
                    setPage(0);
                  }}
                  rowsPerPageOptions={[100, 500, 1000]}
                />
              )}

              <Separator />

              <LabelValuePair label="Performed By" value={auditLog.performerEmailAddress || auditLog.performedBy} />
              <LabelValuePair label="Event Date" value={auditLog.eventDate} />
            </div>
          </>
        ) : (
          <div />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewDeletionRequestedAuditLogDetailsModal;
